import { formatMoney } from '@theme/money-formatting';

// Only descriptive context may travel through URLs. Price, MOQ and currency fields are
// filled from the rendered product page alone, so a crafted link cannot inject them
// into the enquiry email.
const B2B_URL_CONTEXT_KEYS = [
  'product',
  'product_id',
  'handle',
  'variant',
  'sku',
  'order_type',
  'quantity',
  'customization',
  'source',
];

let productQuantityRoot = null;

function formatQuantity(value) {
  return new Intl.NumberFormat('fr-FR').format(value);
}

// France-stock products count in their sales unit (custom.unite_de_vente, « pièces » by default).
function unitsLabel(root) {
  return root?.dataset.unitPlural || 'unités';
}

// Same wording as the server-rendered hint and error in blocks/product-commercial-status.liquid.
function quantityError(root) {
  const min = formatQuantity(Number(root.dataset.min));
  const step = Number(root.dataset.step);
  return step > 1
    ? 'Choisissez un multiple de ' + formatQuantity(step) + ' à partir de ' + min + ' ' + unitsLabel(root) + '.'
    : 'Indiquez au moins ' + min + ' ' + unitsLabel(root) + '.';
}

function quantityHint(root) {
  const step = Number(root.dataset.step);
  const hint = 'Minimum ' + formatQuantity(Number(root.dataset.min)) + ' ' + unitsLabel(root);
  return step > 1 ? hint + ', par multiples de ' + formatQuantity(step) : hint;
}

// Public tiers of a France-stock product (decision D18), rendered by snippets/atelier-paliers.liquid.
// Formatting goes through Horizon's formatMoney with the shop's money format, like Liquid's money filter.
function paliersData(root) {
  if (root.b2bPaliers !== undefined) return root.b2bPaliers;
  root.b2bPaliers = null;
  const script = root.querySelector('[data-b2b-paliers]');
  if (script) {
    try {
      const data = JSON.parse(script.textContent);
      if (Array.isArray(data.tiers) && data.tiers.length > 0) root.b2bPaliers = data;
    } catch (_error) {
      // Without valid tiers the server-rendered summary simply stays as it is.
    }
  }
  return root.b2bPaliers;
}

function paliersMoney(data, cents) {
  return formatMoney(cents, data.moneyFormat || '{{amount_with_comma_separator}} €', data.currency || 'EUR');
}

function updatePaliers(root, rawValue) {
  const data = paliersData(root);
  if (!data) return;
  const parsed = Number(rawValue);
  const quantity = Number.isSafeInteger(parsed) && parsed > 0 ? parsed : data.minimum;
  const current = data.tiers.reduce((match, tier) => (quantity >= tier.min ? tier : match), data.tiers[0]);
  const next = data.tiers.find((tier) => tier.min > quantity);

  root.querySelectorAll('[data-b2b-palier]').forEach((row) => {
    const isCurrent = Number(row.dataset.b2bPalier) === current.min;
    row.classList.toggle('is-current', isCurrent);
    if (isCurrent) row.setAttribute('aria-current', 'true');
    else row.removeAttribute('aria-current');
  });

  const summary = root.querySelector('[data-b2b-paliers-summary]');
  if (summary) {
    summary.textContent =
      'Prix unitaire : ' + paliersMoney(data, current.cents) + ' HT · Total : ' + paliersMoney(data, current.cents * quantity) + ' HT';
  }
  const nextLine = root.querySelector('[data-b2b-paliers-next]');
  if (nextLine) {
    nextLine.hidden = !next;
    const gap = next ? next.min - quantity : 0;
    nextLine.textContent = next
      ? 'Plus que ' + formatQuantity(gap) + ' ' + (gap === 1 ? data.unit : data.units) + ' pour passer à ' + paliersMoney(data, next.cents)
      : '';
  }
}

function decimalMoney(cents) {
  const value = Number(cents);
  return Number.isFinite(value) && value > 0 ? (value / 100).toFixed(2) : '';
}

function isValidProductQuantity(root, rawValue) {
  const value = Number(rawValue);
  const min = Number(root.dataset.min);
  const step = Number(root.dataset.step);
  return /^\d+$/.test(String(rawValue)) && Number.isSafeInteger(value) && value >= min && step > 0 && value % step === 0;
}

function updateRequestSummary(root, quantity) {
  const orderType = root.dataset.orderType === 'personalised' ? 'personalised' : 'standard';
  const summaryOrderType = document.querySelector('[data-b2b-summary-order-type]');
  const summaryQuantity = document.querySelector('[data-b2b-summary-quantity]');
  const summarySurcharge = document.querySelector('[data-b2b-summary-surcharge]');

  if (summaryOrderType) summaryOrderType.textContent = orderType === 'personalised' ? 'Personnalisation' : 'Commande standard';
  if (summaryQuantity) summaryQuantity.textContent = formatQuantity(quantity) + ' ' + unitsLabel(root);
  if (summarySurcharge) {
    const surcharge = Number(root.dataset.customizationSurchargeCents);
    const showSurcharge = orderType === 'personalised' && surcharge > 0;
    summarySurcharge.hidden = !showSurcharge;
    if (showSurcharge) {
      const currency = root.dataset.currency || 'EUR';
      const formatted = new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format(surcharge / 100);
      summarySurcharge.textContent = 'Personnalisation : à partir de ' + formatted + ' HT / unité';
    }
  }
}

function setQuantityNote(root, text) {
  const note = root.querySelector('[data-b2b-quantity-note]');
  if (note) note.textContent = text;
}

function syncProductQuantity(root, rawValue, origin) {
  // Any quantity change clears the « quantité ajustée » note; setOrderType sets it again when needed.
  setQuantityNote(root, '');
  const valid = isValidProductQuantity(root, rawValue);
  const input = root.querySelector('[data-b2b-quantity-input]');
  const error = root.querySelector('[data-b2b-quantity-error]');
  const hint = root.querySelector('[data-b2b-quantity-hint]');
  const minus = root.querySelector('[data-b2b-step="-1"]');
  if (origin !== input) input.value = rawValue;
  input.setCustomValidity(valid ? '' : quantityError(root));
  input.min = root.dataset.min;
  error.textContent = quantityError(root);
  error.hidden = valid;
  if (hint) hint.textContent = quantityHint(root);
  if (minus) minus.disabled = !valid || Number(rawValue) <= Number(root.dataset.min);
  updatePaliers(root, rawValue);

  const formQuantity = document.querySelector('[data-b2b-product-quote-quantity]');
  if (formQuantity) {
    if (origin !== formQuantity) formQuantity.value = rawValue;
    formQuantity.min = root.dataset.min;
    if (formQuantity.type !== 'hidden') formQuantity.setCustomValidity(valid ? '' : quantityError(root));
  }
  if (valid) {
    const quantity = Number(rawValue);
    updateRequestSummary(root, quantity);
    fillForms(productContext(root));
  }
  return valid;
}

// announce: the buyer switched the mode, so an automatic quantity change is said aloud (PP-26).
function setOrderType(root, orderType, announce = false) {
  const normalizedType = orderType === 'personalised' ? 'personalised' : 'standard';
  const input = root.querySelector('[data-b2b-quantity-input]');
  const step = Number(root.dataset.step);
  const minimum = Number(normalizedType === 'personalised' ? root.dataset.personalisedMin : root.dataset.standardMin);
  const current = Number(input.value);
  let next = Number.isSafeInteger(current) && current > 0 ? current : minimum;

  root.dataset.orderType = normalizedType;
  root.dataset.min = String(minimum);
  root.querySelectorAll('[data-b2b-order-type]').forEach((radio) => {
    radio.checked = radio.value === normalizedType;
  });

  if (next < minimum) next = minimum;
  if (next % step !== 0) next = Math.ceil(next / step) * step;
  syncProductQuantity(root, String(next));
  if (announce && Number.isSafeInteger(current) && current > 0 && next !== current) {
    setQuantityNote(
      root,
      normalizedType === 'personalised'
        ? 'Quantité ajustée au minimum de personnalisation (' + formatQuantity(minimum) + ' ' + unitsLabel(root) + ').'
        : 'Quantité ajustée à ' + formatQuantity(next) + ' ' + unitsLabel(root) + '.'
    );
  }
}

function enhanceQuoteQuantityField() {
  const formQuantity = document.querySelector('[data-b2b-product-quote-quantity]');
  if (!formQuantity || !productQuantityRoot || formQuantity.dataset.b2bEnhanced) return;
  formQuantity.dataset.b2bEnhanced = 'true';
  formQuantity.type = 'hidden';
  formQuantity.required = false;
  const formQuantityLabel = formQuantity.closest('.b2b-form__product-quantity');
  if (formQuantityLabel) formQuantityLabel.hidden = true;
  formQuantity.addEventListener('input', () => {
    if (productQuantityRoot) syncProductQuantity(productQuantityRoot, formQuantity.value, formQuantity);
  });
}

// Runs on page load and again when the Theme Editor re-renders a section, so the
// replaced markup is enhanced; already enhanced roots and fields are skipped.
function initProductQuantity() {
  const root = document.querySelector('[data-b2b-quantity-root]');
  const quantityInput = root?.querySelector('[data-b2b-quantity-input]');
  if (!root || !quantityInput) {
    productQuantityRoot = null;
    return;
  }
  productQuantityRoot = root;
  if (!root.classList.contains('is-enhanced')) {
    root.classList.add('is-enhanced');
    root.querySelectorAll('[data-b2b-step]').forEach((button) => { button.hidden = false; });

    root.querySelectorAll('[data-b2b-order-type]').forEach((radio) => {
      radio.addEventListener('change', () => {
        if (radio.checked) setOrderType(root, radio.value, true);
      });
    });

    root.addEventListener('click', (event) => {
      const stepButton = event.target.closest('[data-b2b-step]');
      if (!stepButton) return;
      const step = Number(root.dataset.step);
      const current = isValidProductQuantity(root, quantityInput.value)
        ? Number(quantityInput.value)
        : Number(root.dataset.min);
      const next = Math.max(Number(root.dataset.min), current + Number(stepButton.dataset.b2bStep) * step);
      syncProductQuantity(root, String(next));
    });
    quantityInput.addEventListener('input', () => syncProductQuantity(root, quantityInput.value, quantityInput));
  }
  enhanceQuoteQuantityField();
  // A prospecting link can open the Personnalisé mode with ?order_type=personalised (audit PP-21).
  // Without a selector (products sold only personalised), the rendered mode applies.
  const requestedType = new URLSearchParams(window.location.search).get('order_type');
  const canPersonalise = Boolean(root.querySelector('[data-b2b-order-type][value="personalised"]'));
  const initialType =
    requestedType === 'personalised' && canPersonalise
      ? 'personalised'
      : root.querySelector('[data-b2b-order-type]:checked')?.value || root.dataset.orderType;
  setOrderType(root, initialType);
}

initProductQuantity();
document.addEventListener('shopify:section:load', initProductQuantity);

document.addEventListener('click', (event) => {
  const root = productQuantityRoot;
  const quoteLink = event.target.closest('.atelier-product-overview__quote');
  if (!root || !quoteLink || !root.contains(quoteLink)) return;
  const quantityInput = root.querySelector('[data-b2b-quantity-input]');
  if (syncProductQuantity(root, quantityInput.value, quantityInput)) return;
  event.preventDefault();
  event.stopPropagation();
  quantityInput.focus();
  quantityInput.reportValidity();
}, true);

document.addEventListener('submit', (event) => {
  const root = productQuantityRoot;
  const form = event.target.closest('form[data-b2b-form]');
  const field = form?.querySelector('[data-b2b-product-quote-quantity]');
  if (!root || !field) return;
  if (isValidProductQuantity(root, field.value)) {
    field.setCustomValidity('');
    syncProductQuantity(root, field.value, field);
    return;
  }
  event.preventDefault();
  const quantityInput = root.querySelector('[data-b2b-quantity-input]');
  syncProductQuantity(root, field.value, quantityInput);
  quantityInput.focus();
  quantityInput.reportValidity();
}, true);

function productContext(origin) {
  const contextualRoot = origin?.closest?.('[data-b2b-product-context]');
  const productRoots = document.querySelectorAll('[data-b2b-product-context]');
  const root = contextualRoot || (productRoots.length === 1 ? productRoots[0] : null);
  if (!root) return {};

  const variantInput =
    root.querySelector('form[action*="/cart/add"] input[name="id"]') ||
    document.querySelector('form[action*="/cart/add"] input[name="id"]');
  const quantityInput =
    root.querySelector('[data-b2b-quantity-input]') ||
    root.querySelector('form[action*="/cart/add"] input[name="quantity"]') ||
    document.querySelector('form[action*="/cart/add"] input[name="quantity"]');
  const variantId = variantInput?.value || root.dataset.variantId || '';
  let sku = root.dataset.variantSku || '';
  const variantData = root.querySelector('[data-b2b-variant-data]');

  if (variantData && variantId) {
    try {
      const variants = JSON.parse(variantData.textContent);
      sku = variants.find((variant) => String(variant.id) === String(variantId))?.sku || sku;
    } catch (_error) {
      // Invalid optional context data must never block a form or purchase action.
    }
  }

  return {
    product: root.dataset.productTitle || '',
    handle: root.dataset.productHandle || '',
    product_id: root.dataset.productId || '',
    variant: variantId,
    sku,
    order_type: root.dataset.orderType || '',
    quantity: quantityInput?.value || '',
    standard_unit_price: decimalMoney(root.dataset.standardUnitPriceCents),
    customization_surcharge_from: root.dataset.orderType === 'personalised'
      ? decimalMoney(root.dataset.customizationSurchargeCents)
      : '',
    relevant_moq: root.dataset.min || '',
    currency: root.dataset.currency || '',
    source: root.dataset.sourcePage || window.location.pathname,
  };
}

function urlContext() {
  const params = new URLSearchParams(window.location.search);
  return B2B_URL_CONTEXT_KEYS.reduce((context, key) => {
    context[key] = params.get(key) || '';
    return context;
  }, {});
}

function mergedContext(origin) {
  const currentProductContext = productContext(origin);

  return {
    ...urlContext(),
    ...Object.fromEntries(Object.entries(currentProductContext).filter(([, value]) => value !== '')),
    order_type: origin?.dataset.b2bOrderType || currentProductContext.order_type || urlContext().order_type,
    quantity: origin?.dataset.b2bQuantity || currentProductContext.quantity || urlContext().quantity,
    customization: origin?.dataset.b2bCustomization || urlContext().customization,
  };
}

function fillForms(context) {
  const synchronizedKeys = [
    'source',
    'handle',
    'product_id',
    'variant',
    'sku',
    'order_type',
    'quantity',
    'standard_unit_price',
    'customization_surcharge_from',
    'relevant_moq',
    'currency',
  ];
  document.querySelectorAll('form[data-b2b-form]').forEach((form) => {
    Object.entries(context).forEach(([key, value]) => {
      if (!value && !synchronizedKeys.includes(key)) return;
      // Without a ?source= parameter or a product page, keep the page URL rendered by Liquid.
      if (!value && key === 'source') return;
      const field = form.querySelector(`[data-b2b-field="${key}"]`);
      if (field && (!field.value || synchronizedKeys.includes(key))) {
        field.value = value;
        // Empty hidden context fields stay disabled so the enquiry e-mail has no blank lines (F-05).
        if (field.type === 'hidden') field.disabled = field.value === '';
        if (key === 'quantity' && productQuantityRoot && field.matches('[data-b2b-product-quote-quantity]')) {
          field.setCustomValidity(isValidProductQuantity(productQuantityRoot, value) ? '' : quantityError(productQuantityRoot));
        }
      }
    });

    if (new URLSearchParams(window.location.search).get('request') === 'reassort') {
      const requestType = form.querySelector('[data-b2b-field="request_type"]');
      if (requestType) requestType.value = 'Demande de réassort';
    }
  });
}

// The réassort link on a product page turns the enquiry into a reorder request; any other
// quote CTA turns it back into the form's own request type.
function setRequestType(kind) {
  document.querySelectorAll('form[data-b2b-form]').forEach((form) => {
    const field = form.querySelector('[data-b2b-field="request_type"]');
    if (field) {
      if (field.dataset.b2bDefaultValue === undefined) field.dataset.b2bDefaultValue = field.value;
      field.value = kind === 'reassort' ? 'Demande de réassort' : field.dataset.b2bDefaultValue;
    }
    const summaryRequest = form.querySelector('[data-b2b-summary-request]');
    if (summaryRequest) summaryRequest.hidden = kind !== 'reassort';
  });
}

// dataLayer pushes wait for Shopify's Customer Privacy API and are dropped unless the
// visitor allows analytics processing. DOM b2b:* events stay local and are always sent.
const pendingAnalyticsEvents = [];
let privacyApiSettled = false;

function analyticsAllowed() {
  const privacy = window.Shopify?.customerPrivacy;
  if (typeof privacy?.analyticsProcessingAllowed !== 'function') return false;
  try {
    return Boolean(privacy.analyticsProcessingAllowed());
  } catch (_error) {
    return false;
  }
}

function flushAnalyticsEvents() {
  if (!privacyApiSettled) return;
  const allowed = analyticsAllowed() && Array.isArray(window.dataLayer);
  pendingAnalyticsEvents.splice(0).forEach((payload) => {
    if (allowed) window.dataLayer.push(payload);
  });
}

function settlePrivacyApi() {
  privacyApiSettled = true;
  flushAnalyticsEvents();
}

if (typeof window.Shopify?.loadFeatures === 'function') {
  try {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], settlePrivacyApi);
  } catch (_error) {
    settlePrivacyApi();
  }
} else {
  settlePrivacyApi();
}

function emit(eventName, context = {}) {
  if (!eventName) return;
  const detail = { ...context, source_page: context.source || window.location.pathname };
  document.dispatchEvent(new CustomEvent(`b2b:${eventName}`, { detail }));
  pendingAnalyticsEvents.push({ event: eventName, ...detail });
  flushAnalyticsEvents();
}

document.addEventListener('click', (event) => {
  const explicitTarget = event.target.closest('[data-b2b-event]');
  const communicationTarget = event.target.closest('a[href^="mailto:"], a[href^="tel:"]');
  const catalogTarget = event.target.closest('[data-b2b-catalog-download]');
  const trackedTarget = explicitTarget || communicationTarget || catalogTarget;
  if (!trackedTarget) return;

  let eventName = trackedTarget.dataset.b2bEvent || '';
  if (!eventName && trackedTarget.matches('a[href^="mailto:"]')) eventName = 'email_click';
  if (!eventName && trackedTarget.matches('a[href^="tel:"]')) eventName = 'phone_click';
  if (!eventName && trackedTarget.matches('[data-b2b-catalog-download]')) eventName = 'catalog_download';

  const contextLink = trackedTarget.matches('[data-b2b-context-link]') ? trackedTarget : null;
  const context = mergedContext(trackedTarget);
  fillForms(context);
  if (trackedTarget.dataset.b2bRequest === 'reassort') setRequestType('reassort');
  else if (eventName === 'quote_request_click') setRequestType('quote');

  if (
    contextLink &&
    (!contextLink.hash ||
      contextLink.origin !== window.location.origin ||
      contextLink.pathname !== window.location.pathname)
  ) {
    const target = new URL(contextLink.href, window.location.origin);
    B2B_URL_CONTEXT_KEYS.forEach((key) => {
      if (context[key]) target.searchParams.set(key, context[key]);
    });
    contextLink.href = target.toString();
  }

  if (contextLink?.hash === '#request-quote' && contextLink.pathname === window.location.pathname) {
    const quoteSection = document.getElementById('request-quote');
    if (quoteSection) {
      event.preventDefault();
      quoteSection.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'start',
      });
      history.replaceState(null, '', '#request-quote');
    }
  }

  emit(eventName, context);
});

document.addEventListener('submit', (event) => {
  const form = event.target.closest('form[data-b2b-form]');
  if (!form || event.defaultPrevented || !form.checkValidity()) return;
  emit(`${form.dataset.b2bForm}_request_start`, mergedContext());
});

document.addEventListener('cart:update', (event) => {
  const data = event.detail?.data;
  if (data?.source !== 'product-form-component' || data.didError) return;

  emit('add_to_cart', {
    ...productContext(),
    product_id: data.productId || productContext().product_id || '',
    variant: data.variantId || productContext().variant || '',
    quantity: data.itemCount || productContext().quantity || '',
  });
});

fillForms({ ...urlContext(), ...productContext() });

document.querySelectorAll('[data-b2b-success-event]').forEach((status) => {
  emit(status.dataset.b2bSuccessEvent, mergedContext());
});

if (document.querySelector('main')?.dataset.template?.startsWith('product')) {
  emit('product_view', productContext());
}

// Cart page order step (snippets/atelier-cart-order.liquid, decision D18): establishment details
// saved as cart attributes, format checks, CGV confirmation time, and a checkout button that stays
// disabled until the lines (checked server-side) and the details are valid. HTML required/pattern
// still apply without JavaScript.
const CART_FIELD_MESSAGES = {
  siret: 'Le SIRET compte 14 chiffres.',
  tva: 'Format attendu : FR, 2 caractères, puis 9 chiffres (ex. FR12345678901).',
};
let cartAttributeTimer = 0;
let cartAttributeSave = Promise.resolve();

function cartAttributeName(field) {
  return field.name.match(/^attributes\[(.+)\]$/)?.[1] || '';
}

function normalizeCartField(field) {
  if (field.dataset.b2bValidate === 'siret') return field.value.replace(/\s+/g, '');
  if (field.dataset.b2bValidate === 'tva') return field.value.replace(/\s+/g, '').toUpperCase();
  return field.value.trim();
}

function cartFieldValid(field) {
  if (field.matches('[data-b2b-cgv]')) return field.checked;
  const value = normalizeCartField(field);
  let formatValid = true;
  if (field.dataset.b2bValidate === 'siret') formatValid = /^\d{14}$/.test(value);
  if (field.dataset.b2bValidate === 'tva') formatValid = value === '' || /^FR[0-9A-Z]{2}\d{9}$/.test(value);
  field.setCustomValidity(formatValid ? '' : CART_FIELD_MESSAGES[field.dataset.b2bValidate] || '');
  // aria-describedby may list a hint before the error message.
  const error = (field.getAttribute('aria-describedby') || '')
    .split(/\s+/)
    .filter(Boolean)
    .map((id) => document.getElementById(id))
    .find((element) => element?.classList.contains('atelier-cart-order__error'));
  if (error) error.hidden = formatValid || value === '';
  return formatValid && (!field.required || value !== '');
}

function cartFieldLabel(field) {
  if (field.matches('[data-b2b-cgv]')) return 'acceptation des CGV';
  return field.closest('label')?.querySelector('span')?.textContent.replace('*', '').trim() || field.name;
}

function refreshCartOrder() {
  const order = document.querySelector('[data-b2b-cart-order]');
  if (!order) return;
  const button = order.querySelector('[data-b2b-checkout]');
  const status = order.querySelector('[data-b2b-cart-status]');
  const invalid = [...order.querySelectorAll('[data-b2b-cart-attribute], [data-b2b-cgv]')].filter(
    (field) => !cartFieldValid(field)
  );
  const linesValid = order.dataset.linesValid === 'true';
  if (button) button.disabled = !linesValid || invalid.length > 0;
  if (status) {
    status.textContent =
      linesValid && invalid.length > 0 ? 'Pour valider la commande, complétez : ' + invalid.map(cartFieldLabel).join(', ') + '.' : '';
  }
}

function saveCartAttributes() {
  window.clearTimeout(cartAttributeTimer);
  const order = document.querySelector('[data-b2b-cart-order]');
  if (!order) return cartAttributeSave;
  const attributes = {};
  order.querySelectorAll('[data-b2b-cart-attribute]').forEach((field) => {
    const name = cartAttributeName(field);
    if (name) attributes[name] = normalizeCartField(field);
  });
  const url = window.Theme?.routes?.cart_update_url || '/cart/update.js';
  cartAttributeSave = cartAttributeSave
    .then(() =>
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes }),
      })
    )
    .catch(() => {
      // The checkout form posts the same fields; a failed background save loses nothing.
    });
  return cartAttributeSave;
}

document.addEventListener('input', (event) => {
  const field = event.target.closest?.('[data-b2b-cart-attribute]');
  if (!field) return;
  refreshCartOrder();
  window.clearTimeout(cartAttributeTimer);
  cartAttributeTimer = window.setTimeout(saveCartAttributes, 400);
});

document.addEventListener('change', (event) => {
  const field = event.target.closest?.('[data-b2b-cart-attribute], [data-b2b-cgv]');
  if (!field) return;
  if (field.matches('[data-b2b-cgv]')) {
    // The order keeps the moment the buyer confirmed the order and the CGV.
    if (field.checked) {
      field.value =
        new Date().toLocaleString('fr-FR', { timeZone: 'Europe/Paris', dateStyle: 'short', timeStyle: 'medium' }) +
        ' (heure de Paris)';
    }
  } else if (field.dataset.b2bValidate) {
    field.value = normalizeCartField(field);
  }
  refreshCartOrder();
});

document.addEventListener('click', async (event) => {
  const link = event.target.closest?.('[data-b2b-devis-link]');
  if (!link) return;
  // The printable quote reads the saved cart attributes: save before leaving.
  event.preventDefault();
  await saveCartAttributes();
  window.location.href = link.href;
});

if (document.querySelector('[data-b2b-cart-order]')) {
  refreshCartOrder();
  // Quantity changes morph the summary: re-apply the button state afterwards.
  let cartRefreshQueued = false;
  new MutationObserver(() => {
    if (cartRefreshQueued) return;
    cartRefreshQueued = true;
    window.requestAnimationFrame(() => {
      cartRefreshQueued = false;
      refreshCartOrder();
    });
  }).observe(document.querySelector('[data-b2b-cart-order]').closest('.cart-page') || document.body, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['data-lines-valid', 'disabled'],
  });
}

const mobileSampleCta = document.querySelector('.b2b-mobile-quote');

if (mobileSampleCta) {
  // On product pages the fixed button would also cover the mode selector, the price panel or the quantity control,
  // and on non-catalogue products (D5) Horizon's variant picker and buy buttons; on the homepage, the stock
  // assortment's order button; on the cart page, the establishment details of the order step.
  const overlapSelectors =
    'footer, form[data-b2b-form], .atelier-product-overview__quote, .atelier-product-overview__commercial, .atelier-product-overview__request, .product-details variant-picker, .product-details .buy-buttons-block, .atelier-stock__order, .atelier-cart-order';
  const intersectionState = new Map();
  const privacySelectors = [
    '#shopify-pc__banner',
    '.shopify-pc__banner__dialog',
    '[data-shopify-privacy-banner]',
  ];

  const isVisible = (element) => {
    if (!element) return false;
    const style = window.getComputedStyle(element);
    const bounds = element.getBoundingClientRect();
    return style.display !== 'none' && style.visibility !== 'hidden' && bounds.width > 0 && bounds.height > 0;
  };

  let updateQueued = false;
  const updateMobileSampleCta = () => {
    updateQueued = false;
    const intersectionBlocked = [...intersectionState.values()].some(Boolean);
    const viewportBlocked = [...document.querySelectorAll(`${overlapSelectors}, .atelier-hero__stage`)].some((element) => {
      const bounds = element.getBoundingClientRect();
      return bounds.bottom > 0 && bounds.top < window.innerHeight;
    });
    const dialogBlocked = Boolean(document.querySelector('dialog[open]'));
    const privacyBlocked = privacySelectors.some((selector) =>
      [...document.querySelectorAll(selector)].some(isVisible)
    );

    mobileSampleCta.classList.toggle(
      'b2b-mobile-quote--suppressed',
      intersectionBlocked || viewportBlocked || dialogBlocked || privacyBlocked
    );
  };

  const queueMobileSampleCtaUpdate = () => {
    if (updateQueued) return;
    updateQueued = true;
    window.requestAnimationFrame(updateMobileSampleCta);
  };

  if ('IntersectionObserver' in window) {
    const overlapObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => intersectionState.set(entry.target, entry.isIntersecting));
      queueMobileSampleCtaUpdate();
    });

    document.querySelectorAll(overlapSelectors).forEach((element) => {
      intersectionState.set(element, false);
      overlapObserver.observe(element);
    });
  }

  const visibilityObserver = new MutationObserver(queueMobileSampleCtaUpdate);
  visibilityObserver.observe(document.body, {
    attributes: true,
    childList: true,
    subtree: true,
  });
  window.addEventListener('resize', queueMobileSampleCtaUpdate, { passive: true });
  window.addEventListener('scroll', queueMobileSampleCtaUpdate, { passive: true });
  updateMobileSampleCta();
}
